// Cache for generated ticks to avoid recalculation
const ticksCache = new Map<string, Array<{timestamp: number; label: string}>>();

/**
 * Generates consistent tick values for all charts to ensure they align
 * @param timeRange - Object with start and end timestamps (UTC)
 * @param maxTicks - Maximum number of ticks to display (default 10)
 * @param timezoneOffsetMinutes - Timezone offset in minutes to apply to labels
 * @returns Array of tick objects with timestamp and formatted label
 */
export const generateConsistentTicks = (
  timeRange: {start: number; end: number},
  maxTicks: number = 10,
  timezoneOffsetMinutes: number = 0
): Array<{timestamp: number; label: string}> => {
  // Create cache key from time range, max ticks, and timezone offset
  const cacheKey = `${timeRange.start}-${timeRange.end}-${maxTicks}-${timezoneOffsetMinutes}`;

  // Return cached result if available
  if (ticksCache.has(cacheKey)) {
    return ticksCache.get(cacheKey)!;
  }

  const {start, end} = timeRange;
  const duration = end - start;

  // Calculate ideal interval between ticks
  const idealInterval = duration / (maxTicks - 1);

  // Round to nice intervals (1min, 5min, 10min, 15min, 30min, 1hr)
  const intervals = [
    60 * 1000, // 1 minute
    5 * 60 * 1000, // 5 minutes
    10 * 60 * 1000, // 10 minutes
    15 * 60 * 1000, // 15 minutes
    30 * 60 * 1000, // 30 minutes
    60 * 60 * 1000 // 1 hour
  ];

  // Find the best interval
  let tickInterval = intervals[0];
  for (const interval of intervals) {
    if (idealInterval <= interval) {
      tickInterval = interval;
      break;
    }
  }

  // Generate ticks at regular intervals
  const ticks: Array<{timestamp: number; label: string}> = [];

  // Start from a rounded time
  const firstTick = Math.ceil(start / tickInterval) * tickInterval;

  for (let timestamp = firstTick; timestamp <= end; timestamp += tickInterval) {
    ticks.push({
      timestamp,
      label: formatTickLabel(timestamp, timezoneOffsetMinutes)
    });
  }

  // Always include the start and end points
  if (ticks.length === 0 || ticks[0].timestamp > start) {
    ticks.unshift({
      timestamp: start,
      label: formatTickLabel(start, timezoneOffsetMinutes)
    });
  }

  if (ticks[ticks.length - 1].timestamp < end) {
    ticks.push({
      timestamp: end,
      label: formatTickLabel(end, timezoneOffsetMinutes)
    });
  }

  // Cache the result
  ticksCache.set(cacheKey, ticks);

  // Prevent cache from growing too large (keep last 50 entries)
  if (ticksCache.size > 50) {
    const firstKey = ticksCache.keys().next().value;
    if (firstKey !== undefined) {
      ticksCache.delete(firstKey);
    }
  }

  return ticks;
};

/**
 * Formats a timestamp as hour:minute AM/PM without seconds
 * @param timestamp - Unix timestamp in milliseconds (UTC)
 * @param timezoneOffsetMinutes - Timezone offset in minutes to apply
 * @returns Formatted time string in the specified timezone
 */
export const formatTickLabel = (timestamp: number, timezoneOffsetMinutes: number = 0): string => {
  // Apply timezone offset to convert from UTC to user's local time
  const localizedTimestamp = timestamp + timezoneOffsetMinutes * 60 * 1000;
  const date = new Date(localizedTimestamp);
  const hours = date.getUTCHours(); // Use UTC methods since we already applied offset
  const minutes = date.getUTCMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${displayHours}:${displayMinutes} ${ampm}`;
};

/**
 * Custom tick formatter for Recharts that uses our consistent formatting
 * @param timezoneOffsetMinutes - Timezone offset in minutes to apply to timestamps
 * @param ticksMap - Optional map of timestamp to label for performance
 * @returns Formatted tick label
 */
export const createTickFormatter = (timezoneOffsetMinutes: number = 0, ticksMap?: Map<number, string>) => {
  return (value: any): string => {
    // If we have a pre-computed map, use it
    if (ticksMap && typeof value === "number" && ticksMap.has(value)) {
      return ticksMap.get(value)!;
    }

    // Handle case where value is already a formatted string (from dataKey="time")
    if (typeof value === "string" && value.includes(":")) {
      // Parse the time and reformat without seconds
      const match = value.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)/i);
      if (match) {
        return `${match[1]}:${match[2]} ${match[3]}`;
      }
      return value;
    }

    // Handle timestamp
    if (typeof value === "number") {
      return formatTickLabel(value, timezoneOffsetMinutes);
    }

    return String(value);
  };
};
